import React, { useContext } from 'react';
import logo from '@/assets/logo.png';
import logoDark from '@/assets/logo-dark.png';
import { ThemeContext, getSystemTheme } from '@/contexts/ThemeContext';

export type BrandLogoVariant = 'auto' | 'light' | 'dark';

export interface BrandLogoProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /**
   * `auto` — urmează tema aplicației (light → logo negru, dark → logo alb).
   * `light` — logo cu text negru (fundal deschis).
   * `dark` — logo cu text alb (fundal întunecat), ex. panoul brand de pe login.
   */
  variant?: BrandLogoVariant;
}

function fallbackTheme(): 'light' | 'dark' {
  if (typeof document !== 'undefined') {
    const attr = document.documentElement.getAttribute('data-theme');
    if (attr === 'dark' || attr === 'light') return attr;
    if (document.documentElement.classList.contains('dark')) return 'dark';
  }
  return getSystemTheme();
}

/**
 * Logo-ul complet TLS (cerc galben + „Team Logistic Specialists”),
 * la fel ca în aplicația principală (logo.png / logo-dark.png).
 *
 * Nu cere obligatoriu ThemeProvider — fallback pe data-theme / system
 * (ex. FullscreenLoader din Suspense).
 */
export function BrandLogo({
  variant = 'auto',
  alt = 'Team Logistic Specialists',
  className,
  ...rest
}: BrandLogoProps) {
  const ctx = useContext(ThemeContext);
  const resolved = ctx?.resolvedTheme ?? fallbackTheme();
  const effective =
    variant === 'auto' ? resolved : variant === 'dark' ? 'dark' : 'light';
  // logo-dark.png = text alb (pentru dark mode); logo.png = text negru (light mode)
  const src = effective === 'dark' ? logoDark : logo;

  return <img src={src} alt={alt} className={className} draggable={false} {...rest} />;
}

export default BrandLogo;
